import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Chip, { ChipProps } from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

const CHIP_GAP_PX = 4;
const CHIP_HORIZONTAL_PADDING_PX = 24;
const APPROXIMATE_CHARACTER_WIDTH_PX = 6;
const defaultChipSx = { height: 20, fontSize: "0.7rem" };

const estimateChipWidth = (label: string): number =>
  label.length * APPROXIMATE_CHARACTER_WIDTH_PX + CHIP_HORIZONTAL_PADDING_PX;

export interface ChipBoxItem {
  id: string | number;
  label: string;
}

interface ChipBoxProps<T extends ChipBoxItem> {
  items: T[];
  maxWidth?: string;
  color?: ChipProps["color"];
  variant?: ChipProps["variant"];
  align?: "left" | "center" | "right";
}

const justifyContentByAlign: Record<NonNullable<ChipBoxProps<ChipBoxItem>["align"]>, string> = {
  left: "flex-start",
  center: "center",
  right: "flex-end",
};

const ChipBox = <T extends ChipBoxItem>({
  items,
  maxWidth,
  color = "primary",
  variant = "filled",
  align = "left",
}: ChipBoxProps<T>) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(items.length);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemKey = useMemo(() => items.map((item) => `${item.id}:${item.label}`).join("|"), [items]);

  const updateVisibleCount = useCallback(() => {
    const availableWidth = containerRef.current?.clientWidth ?? 0;
    if (!availableWidth || items.length === 0) {
      setVisibleCount(items.length);
      return;
    }

    const chipWidths = items.map((item) => estimateChipWidth(item.label));
    const allChipsWidth =
      chipWidths.reduce((total, width) => total + width, 0) + CHIP_GAP_PX * Math.max(chipWidths.length - 1, 0);

    if (allChipsWidth <= availableWidth) {
      setVisibleCount(items.length);
      return;
    }

    const moreChipWidth = estimateChipWidth(t("limitations.more_count", { count: items.length }));
    let nextVisibleCount = 0;

    for (let count = items.length - 1; count >= 0; count -= 1) {
      const visibleWidth =
        chipWidths.slice(0, count).reduce((total, width) => total + width, 0) + CHIP_GAP_PX * Math.max(count - 1, 0);
      const totalWidth = visibleWidth + (count > 0 ? CHIP_GAP_PX : 0) + moreChipWidth;

      if (totalWidth <= availableWidth) {
        nextVisibleCount = count;
        break;
      }
    }

    setVisibleCount(nextVisibleCount);
  }, [items, t]);

  useLayoutEffect(() => {
    updateVisibleCount();

    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateVisibleCount);
      return () => window.removeEventListener("resize", updateVisibleCount);
    }

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(element);
    return () => observer.disconnect();
  }, [itemKey, updateVisibleCount]);

  const hiddenItems = items.slice(visibleCount);
  const hiddenCount = hiddenItems.length;
  const countChipLabel =
    visibleCount === 0 ? t("limitations.count", { count: items.length }) : t("limitations.more_count", { count: hiddenCount });

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: justifyContentByAlign[align],
        gap: 0.5,
        overflow: "hidden",
        width: maxWidth ?? "100%",
        maxWidth: "100%",
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      {items.slice(0, visibleCount).map((item) => (
        <Chip key={item.id} label={item.label} size="small" sx={defaultChipSx} color={color} variant={variant} />
      ))}
      {hiddenCount > 0 && (
        <Tooltip title={hiddenItems.map((item) => item.label).join(", ")} placement="top" arrow>
          <Chip label={countChipLabel} size="small" sx={defaultChipSx} />
        </Tooltip>
      )}
    </Box>
  );
};

export default ChipBox;
